/**
 * Merges query params onto an image URL while preserving any existing query
 * string and hash fragment. Shared canonical helper so callers don't blindly
 * append `?${params}` and clobber pre-existing params/anchors.
 */
export const withImageParams = (photoUrl: string, paramsRecord: Record<string, string>): string => {
  const hashIndex = photoUrl.indexOf('#');
  const urlWithoutHash = hashIndex === -1 ? photoUrl : photoUrl.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : photoUrl.slice(hashIndex);
  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const currentQuery = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
  const params = new URLSearchParams(currentQuery);

  Object.entries(paramsRecord).forEach(([key, value]) => {
    params.set(key, value);
  });

  const query = params.toString();
  return `${baseUrl}${query ? `?${query}` : ''}${hash}`;
};

const CONTENTFUL_IMAGE_HOST = 'images.ctfassets.net';
const CONTENTFUL_PASSTHROUGH_EXTENSIONS = new Set(['.gif', '.svg']);

export const normalizeProtocolRelativeImageUrl = (src: string): string => {
  if (src.startsWith('//')) {
    return `https:${src}`;
  }

  return src;
};

const getPathExtension = (pathname: string): string => {
  const filename = pathname.split('/').pop() ?? '';
  const extensionIndex = filename.lastIndexOf('.');

  return extensionIndex === -1 ? '' : filename.slice(extensionIndex).toLowerCase();
};

const shouldOptimizeContentfulImageUrl = (src: string): boolean => {
  try {
    const url = new URL(normalizeProtocolRelativeImageUrl(src));

    return (
      url.hostname === CONTENTFUL_IMAGE_HOST &&
      !CONTENTFUL_PASSTHROUGH_EXTENSIONS.has(getPathExtension(url.pathname))
    );
  } catch {
    return false;
  }
};

export const optimizeContentfulImageUrl = (
  src: string,
  {
    width,
    quality = 75,
  }: {
    width: number;
    quality?: number;
  },
): string => {
  const normalizedSrc = normalizeProtocolRelativeImageUrl(src);

  if (!shouldOptimizeContentfulImageUrl(normalizedSrc)) {
    return normalizedSrc;
  }

  return withImageParams(normalizedSrc, {
    w: String(width),
    q: String(quality),
    fm: 'avif',
  });
};
